import React from 'react';
import { Globe2 } from 'lucide-react';

// Convert 2-letter country code to flag emoji
const flag = (code) => {
  if (!code || code.length !== 2) return '🌐';
  const base = 127397;
  return String.fromCodePoint(...code.toUpperCase().split('').map(c => c.charCodeAt(0) + base));
};

// Country name map (Swedish + Arabic)
const COUNTRY_NAMES = {
  US: { sv: 'USA', ar: 'الولايات المتحدة' }, SE: { sv: 'Sverige', ar: 'السويد' }, GB: { sv: 'Storbritannien', ar: 'المملكة المتحدة' },
  DE: { sv: 'Tyskland', ar: 'ألمانيا' }, FR: { sv: 'Frankrike', ar: 'فرنسا' }, ES: { sv: 'Spanien', ar: 'إسبانيا' },
  IT: { sv: 'Italien', ar: 'إيطاليا' }, NL: { sv: 'Nederländerna', ar: 'هولندا' }, NO: { sv: 'Norge', ar: 'النرويج' },
  DK: { sv: 'Danmark', ar: 'الدنمارك' }, FI: { sv: 'Finland', ar: 'فنلندا' }, PL: { sv: 'Polen', ar: 'بولندا' },
  BR: { sv: 'Brasilien', ar: 'البرازيل' }, MX: { sv: 'Mexiko', ar: 'المكسيك' }, CA: { sv: 'Kanada', ar: 'كندا' },
  JP: { sv: 'Japan', ar: 'اليابان' }, CN: { sv: 'Kina', ar: 'الصين' }, IN: { sv: 'Indien', ar: 'الهند' },
  AU: { sv: 'Australien', ar: 'أستراليا' }, KR: { sv: 'Sydkorea', ar: 'كوريا الجنوبية' },
  SA: { sv: 'Saudiarabien', ar: 'السعودية' }, AE: { sv: 'Förenade Arabemiraten', ar: 'الإمارات' }, EG: { sv: 'Egypten', ar: 'مصر' },
  TR: { sv: 'Turkiet', ar: 'تركيا' }, RU: { sv: 'Ryssland', ar: 'روسيا' }, ID: { sv: 'Indonesien', ar: 'إندونيسيا' },
  TH: { sv: 'Thailand', ar: 'تايلاند' }, VN: { sv: 'Vietnam', ar: 'فيتنام' }, ZA: { sv: 'Sydafrika', ar: 'جنوب أفريقيا' }
};

// Approximate country centroids for map dot positions [x%, y%] on world map
const CENTROIDS = {
  US: [22, 40], CA: [22, 28], MX: [20, 47], BR: [37, 65], AR: [33, 78], CL: [30, 78],
  GB: [47, 30], IE: [45, 30], FR: [49, 35], DE: [51, 33], IT: [52, 39], ES: [47, 40], PT: [45, 40],
  NL: [50, 30], BE: [49, 32], SE: [52, 22], NO: [50, 22], DK: [50, 26], FI: [55, 20], PL: [54, 30], RU: [70, 25],
  CN: [77, 40], JP: [86, 40], KR: [83, 38], IN: [72, 50], PK: [70, 45], TH: [78, 52], VN: [80, 53],
  ID: [82, 62], AU: [85, 72], NZ: [93, 78],
  SA: [58, 50], AE: [62, 51], EG: [56, 48], TR: [58, 38], IL: [57, 45], IR: [63, 44],
  ZA: [55, 76], NG: [50, 58], KE: [58, 62], MA: [46, 45],
  SG: [78, 60], MY: [78, 58], PH: [83, 55], TW: [82, 45], HK: [80, 47]
};

const GeoMap = ({ countries = [], lang = 'sv' }) => {
  const total = countries.reduce((a, c) => a + c.count, 0);
  const max = countries.reduce((a, c) => Math.max(a, c.count), 1);
  const nameOf = (code) => COUNTRY_NAMES[code]?.[lang] || code;

  return (
    <div className="space-y-5">
      {/* Simplified world map with dots */}
      <div className="relative w-full aspect-[2/1] rounded-2xl bg-gradient-to-br from-slate-900 to-neutral-900 overflow-hidden border border-neutral-200">
        <svg viewBox="0 0 100 50" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
          {/* Ultra-simplified continent outlines */}
          <g fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.15)" strokeWidth="0.15">
            {/* North America */}
            <path d="M10,20 Q15,15 22,17 L28,22 L30,32 L25,42 L18,45 L12,38 Q8,30 10,20 Z" />
            {/* South America */}
            <path d="M30,50 L34,50 L38,58 L37,72 L32,80 L28,78 L27,66 L30,50 Z" />
            {/* Europe */}
            <path d="M45,22 L58,22 L57,36 L50,38 L46,34 L45,22 Z" />
            {/* Africa */}
            <path d="M46,42 L58,42 L62,52 L60,65 L54,75 L48,72 L44,60 L46,42 Z" />
            {/* Asia */}
            <path d="M58,20 L88,22 L88,38 L82,48 L72,50 L64,45 L58,36 L58,20 Z" />
            {/* Southeast Asia / Indonesia */}
            <path d="M76,55 L86,55 L88,63 L82,66 L76,63 L76,55 Z" />
            {/* Australia */}
            <path d="M80,68 L92,68 L92,78 L84,80 L80,68 Z" />
          </g>
          {/* Dots for countries */}
          {countries.map(c => {
            const pos = CENTROIDS[c.code];
            if (!pos) return null;
            const size = 0.8 + (c.count / max) * 2.5;
            return (
              <g key={c.code}>
                <circle cx={pos[0]} cy={pos[1]} r={size + 1.5} fill="rgba(6,182,212,0.3)">
                  <animate attributeName="r" values={`${size + 1.5};${size + 3};${size + 1.5}`} dur="2.5s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.6;0.1;0.6" dur="2.5s" repeatCount="indefinite" />
                </circle>
                <circle cx={pos[0]} cy={pos[1]} r={size} fill="#06b6d4" stroke="white" strokeWidth="0.15" />
              </g>
            );
          })}
        </svg>
        <div className="absolute top-3 left-3 rtl:left-auto rtl:right-3 flex items-center gap-1.5 bg-black/60 backdrop-blur rounded-full px-2.5 py-1 text-white text-[10.5px] font-bold">
          <Globe2 className="w-3 h-3" /> {total} {lang === 'sv' ? 'visningar' : 'مشاهدة'}
        </div>
      </div>

      {/* Country list with bars */}
      {countries.length > 0 ? (
        <div className="space-y-2">
          {countries.slice(0, 12).map((c) => (
            <div key={c.code} className="flex items-center gap-3">
              <span className="text-2xl w-8 flex-shrink-0">{flag(c.code)}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[13px] font-bold text-neutral-900 truncate">{nameOf(c.code)}</span>
                  <span className="text-[12px] font-bold text-cyan-600 tabular-nums">{c.count}</span>
                </div>
                <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-cyan-500 to-fuchsia-500 rounded-full" style={{ width: `${(c.count / max) * 100}%` }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-neutral-500 text-[13px]">{lang === 'sv' ? 'Ingen geografisk data ännu' : 'لا توجد بيانات جغرافية بعد'}</div>
      )}
    </div>
  );
};

export default GeoMap;
